import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type UserState } from '@/hooks/useAppStore';

interface HomeHeaderProps {
  user: UserState | null;
  authInitialized: boolean;
}

export default function HomeHeader({ user, authInitialized }: HomeHeaderProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className="sticky top-0 z-50 flex items-center justify-between px-4 pt-safe pb-3 transition-all duration-500"
      style={{
        background: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(0,0,0,0.04)' : '1px solid transparent',
      }}
    >
      {/* Logo */}
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center">
          <i className="ri-quill-pen-fill text-purple-600 text-xl" />
        </div>
        <span className="text-lg font-bold text-gray-900 tracking-tight">
          {t('appName')}
        </span>
      </div>

      {/* 右侧：积分 + 会员 */}
      <div className="flex items-center gap-2">
        {!authInitialized ? (
          <div
            data-testid="home-auth-placeholder"
            className="h-8 w-24 pointer-events-none opacity-0"
            aria-hidden="true"
          />
        ) : user ? (
          <>
            {/* 积分 */}
            <div className="flex items-center gap-1">
              <i className="ri-flashlight-fill text-amber-500 text-sm" />
              <span className="text-sm font-bold text-gray-800">{user.points}</span>
            </div>

            {/* 开通会员 */}
            <button
              onClick={() => navigate('/membership')}
              className="flex items-center gap-1 rounded-full px-3 py-1.5 cursor-pointer whitespace-nowrap transition-all duration-500"
              style={{
                background: user.isVip
                  ? 'linear-gradient(135deg, #D6A456, #C084FC)'
                  : 'linear-gradient(135deg, #F59E0B, #F97316)',
                color: 'white',
                boxShadow: user.isVip
                  ? '0 2px 8px rgba(192,132,252,0.25)'
                  : '0 2px 8px rgba(245,158,11,0.25)',
              }}
            >
              <i className="ri-vip-crown-line text-xs" />
              <span className="text-xs font-bold">
                {user.isVip ? t('home_vipActive', 'VIP') : t('home_openMembership')}
              </span>
            </button>
          </>
        ) : (
          <button
            onClick={() => navigate('/login')}
            className="text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors duration-500"
            style={{ color: '#FF6B9D' }}
          >
            {t('home_login')}
          </button>
        )}
      </div>
    </header>
  );
}
